import { Controller } from "@hotwired/stimulus"
import { get, normalize } from "../forum/api_client"
import { loadTranslations, t } from "../forum/i18n"
import { escapeHtml, loadingSkeletonHtml } from "../forum/renderer"

export default class extends Controller {
  static targets = ["content"]
  static values = { locale: String }

  connect() {
    loadTranslations()
    this.load()
  }

  async load() {
    this.contentTarget.innerHTML = loadingSkeletonHtml(5)
    try {
      const response = await get('/categories')
      this.render(normalize(response.data))
    } catch (e) {
      this.contentTarget.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message)}</div>`
    }
  }

  render(categories) {
    if (!categories || (Array.isArray(categories) && categories.length === 0)) {
      this.contentTarget.innerHTML = `<div class="text-center py-5"><p class="text-muted">${t('no_categories')}</p></div>`
      return
    }

    const items = Array.isArray(categories) ? categories : [categories]
    this.contentTarget.innerHTML = items.map(cat => this.renderCategory(cat)).join('')
    this.bindToggles()
  }

  renderCategory(category) {
    const isInternational = category.scope_level === 'international'
    const collapsed = isInternational ? ' forum-category-collapsed' : ''
    const expanded = !isInternational

    const children = category.children || []
    const forumsToShow = children.length > 0 ? children : [category]

    const subcatsCount = children.length
    const totalTopics = category.total_topics_count || category.topics_count || 0
    const totalPosts = category.total_posts_count || category.posts_count || 0

    return `
      <div class="forum-category-block${collapsed}">
        <h3 class="forum-maintitle d-flex align-items-center justify-content-between">
          <a href="/forum/categories/${escapeHtml(category.slug)}" class="text-decoration-none flex-grow-1">${escapeHtml(category.name)}</a>
          <button class="forum-category-toggle btn btn-link p-0 ms-2" type="button"
                  aria-expanded="${expanded}" aria-label="${t('toggle_category')}"
                  data-forum-categories-target="toggle">
            <i class="fa-solid fa-chevron-down forum-toggle-arrow"></i>
          </button>
        </h3>
        <div class="forum-summary-bar">
          ${t('stats_summary')}: ${t('summary.subcategories', { count: subcatsCount })}, ${t('summary.topics', { count: totalTopics })}, ${t('summary.posts', { count: totalPosts })}
        </div>
        <div class="forum-table-wrap">
          <table class="forum-table">
            <tbody>
              ${forumsToShow.map(f => this.renderForumRow(f)).join('')}
            </tbody>
          </table>
        </div>
      </div>`
  }

  renderForumRow(forum) {
    const iconHtml = forum.icon
      ? `<i class="mdi ${escapeHtml(forum.icon)}" style="color: ${escapeHtml(forum.color || '#6c757d')}; font-size: 1.75rem;"></i>`
      : '<i class="fa-solid fa-folder text-primary" style="font-size: 1.75rem;"></i>'

    const descHtml = forum.description
      ? `<div class="forum-desc">${escapeHtml(forum.description)}</div>`
      : ''

    const subChildren = forum.children || []
    const subforumsHtml = subChildren.length > 0
      ? `<div class="forum-subforums">${subChildren.map(s =>
          `<a href="/forum/categories/${escapeHtml(s.slug)}">${escapeHtml(s.name)}</a>`
        ).join(' · ')}</div>`
      : ''

    const scopeTags = forum.scope_tag_keys || []
    const scopeTagsHtml = scopeTags.length > 0
      ? `<div class="forum-scope-tags">${scopeTags.map(key =>
          `<span class="badge bg-secondary me-1">${t(`scope_tags.${key}`)}</span>`
        ).join('')}</div>`
      : ''

    const topicsCount = forum.topics_count || 0
    const postsCount = forum.posts_count || 0

    return `
      <tr class="forum-forum-row">
        <td class="forum-icon-cell">${iconHtml}</td>
        <td>
          <div class="forum-name">
            <a href="/forum/categories/${escapeHtml(forum.slug)}">${escapeHtml(forum.name)}</a>
          </div>
          ${descHtml}
          ${subforumsHtml}
          ${scopeTagsHtml}
        </td>
        <td class="forum-stats-cell">
          <ul class="forum-stats-list">
            <li>${t('topic_count', { count: topicsCount })}</li>
            <li>${t('post_count', { count: postsCount })}</li>
          </ul>
        </td>
      </tr>`
  }

  bindToggles() {
    this.element.querySelectorAll('.forum-category-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const block = btn.closest('.forum-category-block')
        if (!block) return
        block.classList.toggle('forum-category-collapsed')
        const expanded = !block.classList.contains('forum-category-collapsed')
        btn.setAttribute('aria-expanded', expanded)
      })
    })
  }
}
